import { modelLabel, useTranslation } from '@rhino-project/core/i18n';
import { ReactNode } from 'react';

import { useRenderPaths } from '../../hooks/renderPaths';
import { useModelEditContext } from '@rhino-project/core/hooks';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { ModelEditModalActions } from './ModelEditModalActions';
import { ModelEditSimple, ModelEditSimpleProps } from './ModelEditSimple';
import { ModelFieldGroup } from './ModelFieldGroup';
import { FormErrors } from '../forms/FormErrors';
import { RhinoResourceName } from '@rhino-project/core';
import {
  Fieldset,
  ModalBody,
  ModalBackdrop,
  ModalContainer,
  ModalDialog,
  ModalHeader,
  ModalHeading
} from '@heroui/react';

export const ModelEditModalHeader = (props: { title?: ReactNode }) => {
  const { t } = useTranslation();
  const { model } = useModelEditContext();
  const {
    title = t('rhino.frontend.messages.edit_value', {
      value1: modelLabel(model)
    })
  } = props;

  return (
    <ModalHeader>
      <ModalHeading>{title}</ModalHeading>
    </ModalHeader>
  );
};

export const ModelEditModalForm = ({ ...props }: { paths?: string[] }) => {
  const { paths } = useModelEditContext();
  const renderPaths = useRenderPaths(props.paths || paths, {
    Component: ModelFieldGroup,
    props: { variant: 'secondary' }
  });

  return (
    <ModalBody>
      <Fieldset>
        <Fieldset.Group>{renderPaths}</Fieldset.Group>
      </Fieldset>
      <FormErrors />
    </ModalBody>
  );
};

const defaultComponents = {
  ModelEditModalHeader,
  ModelEditModalForm,
  ModelEditModalActions
};

export interface ModelEditModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  isOpen?: boolean;
  onModalClose?: () => void;
  title?: ReactNode;
  [key: string]: unknown;
}

const ModelEditModalBase = ({
  overrides,
  isOpen = false,
  onModalClose,
  title,
  ...props
}: ModelEditModalProps) => {
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelEditModalHeader, ModelEditModalForm } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) onModalClose?.();
  };

  return (
    <ModalBackdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalContainer>
        <ModalDialog>
          <ModelEditSimple
            fallback={isOpen}
            {...(props as ModelEditSimpleProps<RhinoResourceName>)}
          >
            {/* eslint-disable-next-line @eslint-react/static-components */}
            <ModelEditModalHeader title={title} />
            {/* eslint-disable-next-line @eslint-react/static-components */}
            <ModelEditModalForm />
            <ModelEditModalActions onModalClose={onModalClose} />
          </ModelEditSimple>
        </ModalDialog>
      </ModalContainer>
    </ModalBackdrop>
  );
};

export const ModelEditModal = (props: ModelEditModalProps) =>
  useGlobalComponentForModel('ModelEditModal', ModelEditModalBase, props);
